import React, { useEffect, useState } from "react";
import { Button, Form, Alert, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Box, IconButton, Typography } from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import "../CustomStyle.css";
import CustomDataGrid from "../components/CustomDataGrid";
import EntityDetails from "../components/EntityDetails";
import {
  generateFormData,
  campaignMapping,
  generateGridColumns,
  campaignGridColumns,
  generateGridRows,
  campaignRowMapping
} from "../utils/PageUtils";
import { createCampaign, fetchCampaignData } from "../utils/api";
import PageHelmet from "../components/PageHelmet";
import { campaignInputValidation } from "../utils/PreviewUtils";

function CreateCampaign({ userRole, bearerToken }) {
  const [campaignsData, setCampaignsData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [campaignName, setCampaignName] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState("");
  const [refreshGrid, setRefreshGrid] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadCampaigns = async () => {
      if (!bearerToken) {
        return;
      }
      setIsLoading(true);
      try {
        const result = await fetchCampaignData(bearerToken);
        if (result?.success) {
          const sortedCampaigns = (result.data?.records || []).sort(
            (a, b) => new Date(b.created) - new Date(a.created)
          );
          setCampaignsData(sortedCampaigns);
        } else {
          setError(result?.message || "An error occurred while fetching campaigns.");
        }
      } catch {
        setError("An error occurred while fetching campaigns.");
      } finally {
        setIsLoading(false);
        if (refreshGrid) {
          setRefreshGrid(false);
        }
      }
    };
    if (refreshGrid) {
      loadCampaigns();
    }
  }, [refreshGrid]);

  const handleInputChange = (setter) => (e) => setter(e.target.value);

  const handleRowClick = (e) => {
    const selected = campaignsData[e.id - 1];
    setSelectedCampaign(selected);
    setSelectedRowId(e.id)
  };

  const handleSubmit = async (campaignName, destinationUrl) => {
    setError(null);
    setInfo(null);

    const trimmedCampaignName = campaignName.trim();
    const trimmedDestinationUrl = destinationUrl.trim();

    const validationResult = campaignInputValidation(trimmedCampaignName, trimmedDestinationUrl);
    if (validationResult.error) {
      setError(validationResult.message);
      return;
    }

    const payload = {
      name: trimmedCampaignName,
      destination_url: trimmedDestinationUrl,
    };

    try {
      const response = await createCampaign(payload, bearerToken);

      switch (response.status) {
        case 201:
          setInfo(`A campaign for ${payload.name} was created successfully`);
          setCampaignName("");
          setDestinationUrl("");
          setRefreshGrid(true);
          break;
        case 403: {
          let message;
          if (response?.data?.details) {
            message = `Quota exceeded for ${response.data.details.action}: Limit ${response.data.details.limit}, Consumed ${response.data.details.current_usage}, Available ${response.data.details.limit_available}.\nContact Support to increase your Quota.`
          } else {
            message = response?.data?.message;
          }
          setError(message);
          break;
        }
        case 409:
          setError(`A campaign ${payload.name} already exists for user`);
          break;
        case 422:
          setError(response.data.message || "Campaign name is invalid.")
          break;
        default:
          setError(
            "The operation you attempted has failed. Please contact support."
          );
      }
    } catch (error) {
      setError("The operation has failed. Please contact support.");
    }
  };

  const isFormValid = campaignName.trim() && destinationUrl.trim();

  const campaignFormData = selectedCampaign
    ? generateFormData(selectedCampaign, campaignMapping)
    : null;

  const campaignColumns = generateGridColumns(campaignGridColumns, "campaign_name");

  const campaignRows = generateGridRows(campaignsData, campaignRowMapping);

  if (isLoading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Please wait...</span>
        </Spinner>
      </div>
    );
  }
  return userRole === "customer" ? (
    <>
      <PageHelmet
        title="Create Campaign"
        description="Create a new campaign with TraQR."
        url={`${process.env.REACT_APP_REDIRECT_URI}/campaigns`}
      />
      <div className="campaign-container">
        <div className="create-campaign-container">
          <h1 className="create-campaign-title">Create Campaign</h1>

          <Form className="campaign-form">
            <Form.Group className="form-group-custom" controlId="campaignName">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Form.Label className="custom-label">
                  Name your new campaign
                  <span style={{ color: "red" }}> *</span>
                </Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{`Max ${100} characters`}</Tooltip>}
                >
                  <IconButton size="small" tabIndex={-1} sx={{ color: '#94a3b8', p: 0.5 }}>
                    <HelpOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </OverlayTrigger>
              </Box>

              <Form.Control
                type="text"
                placeholder="For ex:Postcard-ForHayward.Empty/duplicate campaign names are not allowed."
                value={campaignName}
                onChange={handleInputChange(setCampaignName)}
                maxLength={100}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  color: campaignName.length >= 100 ? "#ef4444" : "#94a3b8",
                  fontWeight: 700
                }}>
                {campaignName.length}/{100}
              </Typography>

            </Form.Group>
            <Form.Group
              className="form-group-custom"
              controlId="destinationUrl"
            >
              <Form.Label className="custom-label">
                Enter the URL value to redirect the user to
                <span style={{ color: "red" }}> *</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="For ex: https://yourcompany.com/landingPageX- Cannot be modified later."
                value={destinationUrl}
                onChange={handleInputChange(setDestinationUrl)}
              />
            </Form.Group>
            <div style={{ textAlign: "center" }}>
              <Button
                variant="dark"
                onClick={() => handleSubmit(campaignName, destinationUrl)}
                className="create-campaign-button"
                disabled={!isFormValid}
              >
                Create Campaign
              </Button>
            </div>
          </Form>

          {error && (
            <Alert
              data-testid="Error-alert"
              variant="danger"
              className="alert-message"
              style={{ whiteSpace: "pre-line" }}
            >
              {error}
            </Alert>
          )}
          {info && (
            <Alert
              data-testid="Success-alert"
              variant="success"
              className="alert-message"
            >
              {info}
            </Alert>
          )}
        </div>


        <div className="create-main-container custom-grid-container">
          <div
            className={`create-grid-form ${selectedCampaign ? "grid-to-left" : "grid-centered"
              } `}
          >
            <div className="create-grid-container">
              <h3 className="heading-color">Existing Campaigns</h3>
              <CustomDataGrid
                rows={campaignRows}
                columns={campaignColumns}
                totalRows={campaignsData.length}
                onRowClick={handleRowClick}
                selectedRowId={selectedRowId}
              />
            </div>
            {selectedCampaign && (
              <div className="create-form-container">
                <h3 className="details-heading">Campaign Details</h3>
                <EntityDetails inputs={campaignFormData} width={"100px"} />
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
}

export default CreateCampaign;
